import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import VeratadCapture from "../../face-capture/veratad/capture";
import AndroidCameraBlockPortal from "../../../components/AndroidCameraBlockPortal";
import { getAndroidBlockVariant } from "../../../utils/androidDetection";
import { uploadImageToS3 } from "../../../helpers";
import { DataContext } from "../../../context/DataContext";
import Handoff from "../../handoff";
import CompactHandoff from "../../../components/compact-handoff";
import useDeviceDetection from "../../../hooks/useDeviceDetection";
import { getDeviceSignalsUrl, getEdgeQueueUrl } from "../../../utils/edge";
import useLocale from "../../../hooks/useLocale";
import {
  Text,
  Button,
  Loader,
  LoadingOverlay,
  Stack,
  Group,
  Box,
  ThemeIcon,
  Alert,
  useMantineTheme,
} from "@mantine/core";
import {
  IconBulb,
  IconEye,
  IconEyeglassOff,
  IconFocusCentered,
  IconHelmetOff,
  IconShieldX,
} from "@tabler/icons-react";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";
import VerificationProcessingScreen from "../../../components/verification-processing-screen";
import { preloadFaceDetectionModels } from "../../../hooks/useFaceDetection";
import AgeEstimationStartDrawer from "../../../components/age-estimation-start-drawer";
import { trackEvent } from "../../../utils/telemetry";
import { landscapeEnabled } from "../../../utils/featureFlags";
import useIsLandscape from "../../../hooks/useOrientation";
import { isMobile } from "react-device-detect";

const AgeEstimation = () => {
  const { settings, data, dispatch, text, postPayloadMessage } = useContext(DataContext);
  const theme = useMantineTheme();
  const locale = useLocale("age-estimation");
  // face-capture locale for the Android camera block portal — the
  // androidCameraBlock* keys live in the face-capture namespace (same
  // strings face-capture uses). VeratadCapture loads this internally
  // for the normal flow; we need it here too for force-mode rendering.
  const faceLocale = useLocale("face-capture");
  const { isTrulyMobile, isEmulated, serverTamperDetected, serverCheckPending } = useDeviceDetection({
    requestId: settings?.request_id,
    serverReportUrl: getDeviceSignalsUrl(),
  });
  const blockedLocale = useLocale("device-blocked");
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isReadyForStatusCheck, setIsReadyForStatusCheck] = useState(false);
  const loadingStartTimeRef = useRef(null);
  const [hasError, setHasError] = useState(false);
  // Camera permission failed (denied / OS-blocked / timeout). Routed to
  // the dedicated camera-blocked screens below, NOT the generic
  // upload-failure screen — same routing as the ID-doc flow. The error
  // name picks the screen: NotAllowedError (a permission block) gets the
  // unblock portal; hardware/timeout errors get plain denied guidance,
  // matching capture.js's own internal split.
  const [cameraBlocked, setCameraBlocked] = useState(false);
  const [cameraErrorName, setCameraErrorName] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  // How much of the viewport the live drawer covers from the bottom
  // (its height plus its 16px bottom margin). Measured, not assumed,
  // and handed to VeratadCapture as bottomInset so the oval, the
  // feedback text and the manual-capture button all lay out in the
  // space above the drawer instead of underneath it. Only applied
  // once live: the paused drawer is much taller and the oval behind
  // it is just a placeholder.
  //
  // Landscape phone with FACE_LANDSCAPE on: the drawer docks down the
  // right edge instead (sideLayout) and what is measured is its width
  // plus its 12px right margin, handed over as rightInset so the oval
  // centers in the space left of it.
  const landscape = useIsLandscape();
  const sideLayout = landscapeEnabled() && isMobile && landscape;
  // Callback ref, not useRef: the drawer is not in the tree on the
  // first renders (the server device check shows a loading overlay),
  // and an effect keyed on hasStarted never re-ran when it appeared,
  // so the intro panel was never measured.
  const [drawerEl, setDrawerEl] = useState(null);
  const [drawerInset, setDrawerInset] = useState(0);
  useEffect(() => {
    const el = drawerEl;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setDrawerInset(sideLayout ? rect.width + 12 : rect.height + 16);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [drawerEl, hasStarted, sideLayout]);
  const [status, setStatus] = useState("NEW");
  const [onMobile, setOnMobile] = useState(isTrulyMobile);
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  const [qrScanned, setQrScanned] = useState(false);
  // Kick off the face-api model fetch (tinyFaceDetector + landmark68,
  // ~560KB across 4 self-hosted requests) on intro mount, instead of
  // waiting for the user to click Get Started. The button below stays
  // disabled with a spinner until the preload resolves, so on slow
  // network paths the user waits on the readable intro content
  // instead of staring at a blank camera-init state.
  const [modelsReady, setModelsReady] = useState(false);
  console.log("[AgeEstimation] render — modelsReady:", modelsReady);
  useEffect(() => {
    console.log("[AgeEstimation] mount effect firing — calling preloadFaceDetectionModels()");
    // Funnel top: the intro screen rendered — everything upstream of
    // the Get Started click (capture_started) anchors to this.
    trackEvent("landed", {}, { once: true });
    let cancelled = false;
    preloadFaceDetectionModels()
      .then(() => {
        console.log("[AgeEstimation] preload resolved — modelsReady=true", { cancelled });
        if (!cancelled) setModelsReady(true);
      })
      .catch((err) => {
        console.warn("[AgeEstimation] preload rejected:", err);
        // Unblock button on preload error so a transient failure
        // doesn't permanently strand the user; the hook will retry
        // + surface the real error inside capture.js if it persists.
        if (!cancelled) setModelsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const resolvedPrimaryColor = data?.styles?.primaryColor ?? theme.primaryColor;
  const should_show_continue_on_desktop = settings?.shouldShowContinueOnDesktop === true;
  const isHandoffMode = !onMobile && !should_show_continue_on_desktop;

  // Update onMobile when device detection changes (e.g., emulation toggled)
  useEffect(() => {
    setOnMobile(isTrulyMobile);
  }, [isTrulyMobile]);

  useEffect(() => {
    if (data?.isReactNative) {
      setOnMobile(true);
    }
  }, [data?.isReactNative]);

  // Handler for camera permission granted
  const handleCameraAllowed = useCallback(() => {}, []);

  // Terminal status captured from polling — VerificationProcessingScreen
  // waits for resultReady before advancing to "Completing verification…"
  // and only then fires onComplete which dispatches navigation.
  const [terminalStatus, setTerminalStatus] = useState(null);

  const captureTerminal = useCallback((result) => {
    if (!result) return true;
    const status = result.status;
    const processState = result.process_state;
    if (processState === "NEXT_APP_TRIGGER" || processState === "COMPLETED" ||
        status === "PASS" || status === "FAIL") {
      trackEvent("verification_done", { status, process_state: processState });
    }
    if (processState === "NEXT_APP_TRIGGER") {
      setTerminalStatus("NEXT_APP");
      return false;
    }
    if (processState === "COMPLETED") {
      setTerminalStatus(status === "PASS" ? "PASS" : "FAIL");
      return false;
    }
    if (status === "PASS") {
      setTerminalStatus("PASS");
      return false;
    }
    if (status === "FAIL") {
      setTerminalStatus("FAIL");
      return false;
    }
    return true;
  }, []);

  const handleProcessingComplete = useCallback(() => {
    if (terminalStatus === "NEXT_APP") {
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      dispatch({ type: "TRIGGER_NAVIGATION" });
      return;
    }
    dispatch({ type: "SET_STATUS", payload: terminalStatus || "FAIL" });
    dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
  }, [terminalStatus, dispatch]);

  // Use edge status check if edge mode is enabled
  if (data?.edge === true) {
    useEdgeStatusCheck(isReadyForStatusCheck, captureTerminal);
  } else {
    useStatusCheck(isReadyForStatusCheck, false, "agematch/estimate", captureTerminal);
  }

  const handleLoading = () => {
    loadingStartTimeRef.current = Date.now();
    setIsLoading(true);
  };

  const handleComplete = () => {
    // Build the request payload as a fresh object instead of
    // mutating the `data` context. Mutating context bypasses
    // React's state pipeline and can cause stale-read bugs in
    // any other consumer of DataContext. The fetch only needs
    // these fields locally so there's no reason to push them
    // back into shared state.
    const payload = {
      ...data,
      request_id: settings.request_id,
      type: "SDK_SCAN",
      country: settings.country,
      front_ext: data?.front_ext || "jpeg",
      back_ext: data?.back_ext || "jpeg",
    };
    setIsComplete(true);

    // Delay status check until ~5s after the loading animation started
    const elapsed = Date.now() - (loadingStartTimeRef.current || Date.now());
    const remainingDelay = Math.max(0, 5000 - elapsed);
    setTimeout(() => {
      setIsReadyForStatusCheck(true);
    }, remainingDelay);

    // Send postMessage that verification has been initiated
    if (postPayloadMessage) {
      postPayloadMessage({
        event: "VERIFICATION_INITIATED",
        app: "agematch/estimate",
      });
    }

    // Check if edge mode is enabled
    const edgeQueueUrl = getEdgeQueueUrl();

    const endpoint = data?.edge === true
      ? edgeQueueUrl
      : process.env.REACT_APP_BACKEND_URL + "/v3/stable/api/frontend/estimate/process.php";

    // Add queue parameter for edge mode
    const requestBody = data?.edge === true
      ? { ...payload, queue: "estimate" }
      : payload;

    trackEvent("processing_requested");
    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {})
      .catch(function () {
        alert("Something Went Wrong! Please try again.");
      });
    // Additional code if needed when the image processing is complete
  };

  const handleError = (error) => {
    setIsLoading(false);
    // Camera-permission failures get the camera-blocked recovery UI
    // (unblock steps + camera-app fallback) instead of the generic
    // "Verification Failed" upload screen. VeratadCapture reports them
    // as type "camera_error" (NotAllowedError, NotReadableError,
    // permission timeout, Android OS toggle).
    if (error?.type === "camera_error") {
      setCameraErrorName(error?.name || null);
      setCameraBlocked(true);
      return;
    }
    setHasError(true);
  };

  // Handler for Veratad capture — uploads to S3 then triggers the same flow as Yoti
  const handleVeratadCapture = useCallback(
    async (blobOrDataUrl, _previewUrl, _frameChain) => {
      handleLoading();
      try {
        // Support both blob (new capture.js) and data URL string (legacy)
        let blob = blobOrDataUrl;
        if (typeof blob === "string") {
          const [header, b64] = blob.split(",");
          const mime = header.match(/:(.*?);/)?.[1] || "image/jpeg";
          const binary = atob(b64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }
          blob = new Blob([bytes], { type: mime });
        }
        const imageKey = `${settings.request_id}_dl_selfie.jpeg`;
        trackEvent("upload_started", { bytes: blob?.size });
        const uploadStart = performance.now();
        await uploadImageToS3(imageKey, blob, settings);
        trackEvent("upload_completed", { upload_ms: Math.round(performance.now() - uploadStart) });
        handleComplete();
      } catch (err) {
        console.error("Veratad capture upload failed:", err);
        trackEvent("upload_failed", { error: err?.message });
        handleError({ type: "upload_error", message: err?.message || "Image upload failed" });
      }
    },
    [settings]
  );

  // True after the user has submitted a file via the camera-app fallback.
  // Used to bypass the force_android_block early return so the loading
  // overlay and processing screen can render normally — without this,
  // force mode would keep showing the portal even after the file was
  // uploaded, making users think nothing happened and tap again.
  const [fallbackInProgress, setFallbackInProgress] = useState(false);

  // Camera-app fallback for age estimation: user tapped "Take a selfie with
  // the camera app" on the Android block portal. Route the File through the
  // existing handleVeratadCapture upload pipeline — same S3 bucket, same
  // imageKey, same downstream processing. No frameChain / previewUrl needed.
  const handleSelfieFallback = useCallback(
    (file) => {
      if (!file) return;
      console.log("[AgeEstimate] handleSelfieFallback: file received", file.name, file.size, file.type);
      setFallbackInProgress(true);
      handleVeratadCapture(file, null, null);
    },
    [handleVeratadCapture]
  );

  const handleVeratadReady = useCallback(() => {
    setIsReady(true);
  }, []);

  const handleStart = () => {
    trackEvent("capture_started");
    // Send postMessage for get started click
    if (postPayloadMessage) {
      postPayloadMessage({
        event: "GET_STARTED_CLICKED",
        app: "agematch/estimate",
      });
    }

    setHasStarted(true);
  };

  // Conditional banner logic
  const shouldShowBanner = (settings.site_name === "kid-pre-prod" || settings.site_name === "kid-qa") && 
                           settings.journey?.name && 
                           (settings.journey.name === "estimate_only_roc" || settings.journey.name === "estimate_only_yoti");
  
  const getBannerText = () => {
    if (settings.journey?.name === "estimate_only_roc") return "FAE PROVIDER: ROC";
    if (settings.journey?.name === "estimate_only_yoti") return "FAE PROVIDER: Yoti";
    return "";
  };

  // Real-time face-detection gating pushed up from VeratadCapture
  // so the three icon-and-label columns can light up as each prep
  // item is confirmed by the SDK. The drawer layout itself stays
  // constant in paused and live phases — only the icon colors and
  // the visibility of the Allow button change.
  const [gatingState, setGatingState] = useState({});

  // Two different check sets, one per phase:
  //
  // PAUSED — prep advice the SDK can't directly verify but the
  // user can act on before tapping Allow (remove hat, take off
  // glasses, find good light). Shown without indicator bubbles
  // since these are reminders, not live SDK checks.
  //
  // LIVE — three honest SDK-mapped gates whose green checks
  // accurately reflect what face-api has confirmed:
  //   - Good lighting → `lighting` (luma mean + std-dev)
  //   - In frame     → `facePresent && faceSize && centering`
  //   - Look straight → `pose` (yaw/pitch/roll within limits)
  // Drawer labels go through three priority levels:
  // 1. Publisher text override (`text.estimation.drawer.*` from
  //    DataContext) — lets a customer customize copy per journey.
  // 2. Locale file (`locale.drawer.*`) — gives every non-English
  //    user a real translation, deep-merge-fallback to English.
  // 3. Hardcoded English string — last-resort defensive default.
  const pausedChecks = [
    {
      key: "lighting",
      icon: IconBulb,
      label:
        text.estimation?.drawer?.paused?.lighting ||
        locale?.drawer?.paused?.lighting ||
        "Find\ngood light",
    },
    {
      key: "headwear",
      icon: IconHelmetOff,
      label:
        text.estimation?.drawer?.paused?.headwear ||
        locale?.drawer?.paused?.headwear ||
        "Remove\nheadwear",
    },
    {
      key: "glasses",
      icon: IconEyeglassOff,
      label:
        text.estimation?.drawer?.paused?.glasses ||
        locale?.drawer?.paused?.glasses ||
        "Remove\nglasses",
    },
  ];
  const inFrame =
    !!gatingState?.facePresent &&
    !!gatingState?.faceSize &&
    !!gatingState?.centering;
  const liveChecks = [
    {
      key: "lighting",
      icon: IconBulb,
      label:
        text.estimation?.drawer?.live?.lighting ||
        locale?.drawer?.live?.lighting ||
        "Good lighting",
      // The room-darkness flag from the SDK is the right signal
      // here — it evaluates on every frame regardless of whether
      // a face is detected yet, so the user gets immediate
      // lighting feedback. (The face-region `lighting` gate only
      // flips after `facePresent` does, which makes it lag the
      // "In frame" gate next to it.)
      satisfied: gatingState?.isTooDark === false,
    },
    {
      key: "inFrame",
      icon: IconFocusCentered,
      label:
        text.estimation?.drawer?.live?.inFrame ||
        locale?.drawer?.live?.inFrame ||
        "In frame",
      satisfied: inFrame,
    },
    {
      key: "lookStraight",
      icon: IconEye,
      label:
        text.estimation?.drawer?.live?.lookStraight ||
        locale?.drawer?.live?.lookStraight ||
        "Look straight",
      satisfied: !!gatingState?.pose,
    },
  ];
  const drawerChecks = hasStarted ? liveChecks : pausedChecks;

  const drawerFooter =
    !onMobile && should_show_continue_on_desktop ? (
      <CompactHandoff
        nextPath="agematch/estimate"
        onQrScanned={setQrScanned}
      />
    ) : null;

  // Drawer carries title + icon checks + Allow button. Title only
  // shown in the paused state — once camera is live the directive
  // feedback above the oval is the focal point.
  const startDrawer = (
    <AgeEstimationStartDrawer
      innerRef={setDrawerEl}
      title={
        !hasStarted
          ? text.estimation?.drawer?.title ||
            locale?.drawer?.title ||
            "Get ready to verify your age"
          : null
      }
      checks={drawerChecks}
      showIndicators={hasStarted}
      compact={hasStarted}
      permissionCallout={
        !hasStarted
          ? text.estimation?.drawer?.permissionCallout ||
            locale?.drawer?.permissionCallout ||
            "We only use camera access to capture your face for age verification."
          : null
      }
      showButton={!hasStarted}
      buttonLabel={
        text.estimation?.drawer?.allowCameraButton ||
        locale?.drawer?.allowCameraButton ||
        "Allow Camera Access"
      }
      preparingLabel={
        text.estimation?.drawer?.preparingLabel ||
        locale?.drawer?.preparingLabel ||
        "Getting ready…"
      }
      modelsReady={modelsReady}
      disabled={qrScanned}
      buttonTextColor={data?.styles?.components?.button?.color || "#fff"}
      onStart={handleStart}
      footer={drawerFooter}
      side={sideLayout}
    />
  );

  // Show loading overlay while server-side device check is pending
  if (serverCheckPending) {
    return <LoadingOverlay visible />;
  }

  // Block session if server-side tamper detection flagged this request
  if (serverTamperDetected) {
    return (
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "400px",
          padding: "20px",
        }}
      >
        <ThemeIcon variant="light" color="red" size={80} radius="xl">
          <IconShieldX size={50} stroke={1.5} />
        </ThemeIcon>
        <Text fz={24} fw={700} align="center" mt="lg">
          {blockedLocale?.blocked?.title || "Unable to Continue"}
        </Text>
        <Text fz="md" c="dimmed" align="center" mt="xs" maw={400}>
          {blockedLocale?.blocked?.message || "Please try again on your mobile device."}
        </Text>
      </Box>
    );
  }

  if (hasError) {
    return (
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "400px",
          padding: "20px",
        }}
      >
        <ThemeIcon variant="light" color="red" size={80} radius="xl">
          <IconShieldX size={50} stroke={1.5} />
        </ThemeIcon>
        <Text fz={24} fw={700} align="center" mt="lg">
          {locale?.error?.title || "Verification Failed"}
        </Text>
        <Text fz="md" c="dimmed" align="center" mt="xs" maw={400}>
          {locale?.error?.message || "Something went wrong while uploading your image. Please try again."}
        </Text>
        <Button
          size="lg"
          mt="xl"
          onClick={() => {
            setHasError(false);
            setCameraBlocked(false);
            setCameraErrorName(null);
            setFallbackInProgress(false);
            setHasStarted(true);
          }}
        >
          {locale?.error?.retry || "Try Again"}
        </Button>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <VerificationProcessingScreen
        type="face-age"
        resultReady={terminalStatus !== null}
        onComplete={handleProcessingComplete}
      />
    );
  }

  // Camera permission blocked: same recovery routing the ID-doc flow
  // uses. A NotAllowedError (permission block) gets the block portal —
  // Android variants carry unblock steps, the generic variant offers
  // the camera-app fallback everywhere; other camera errors
  // (NotReadableError, TimeoutError, hardware failures) get the
  // camera-denied guidance with a retry, mirroring capture.js's own
  // NotAllowedError-only portal gate. Every string is an existing
  // translated key (face-capture errors.*, age-estimation error.retry).
  // fallbackInProgress lets the upload overlay take over once the user
  // submits a fallback photo, same as force mode below.
  if (cameraBlocked && !fallbackInProgress) {
    if (cameraErrorName === "NotAllowedError") {
      return (
        <AndroidCameraBlockPortal
          locale={faceLocale}
          requestId={settings.request_id}
          captureType="face"
          isBrowserHandoffSession={false}
          androidVariant={getAndroidBlockVariant()}
          onPhotoFallback={handleSelfieFallback}
          captureMode="user"
        />
      );
    }
    return (
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "400px",
          padding: "20px",
        }}
      >
        <ThemeIcon variant="light" color="red" size={80} radius="xl">
          <IconShieldX size={50} stroke={1.5} />
        </ThemeIcon>
        <Text fz={24} fw={700} align="center" mt="lg">
          {faceLocale?.errors?.cameraErrorTitle || "Camera Access Denied"}
        </Text>
        <Text fz="md" c="dimmed" align="center" mt="xs" maw={400}>
          {faceLocale?.errors?.cameraDenied ||
            "You have denied camera permissions. Please go into settings and allow camera, refresh the page, and try again."}
        </Text>
        <Button
          size="lg"
          mt="xl"
          onClick={() => {
            setCameraBlocked(false);
            setCameraErrorName(null);
            setHasStarted(true);
          }}
        >
          {locale?.error?.retry || "Try Again"}
        </Button>
      </Box>
    );
  }

  // Force mode: render the Android camera block portal directly, bypassing
  // device detection and the entire capture flow. Used by testers to verify
  // the selfie fallback end-to-end without needing a real Android device
  // with the camera actually blocked.
  //
  //   ?force_android_block=os_toggle         → Quick Settings variant
  //   ?force_android_block=chrome_permission → legacy Chrome perms variant
  //   ?force_android_block=true              → defaults to os_toggle
  const forceAndroidBlock = new URLSearchParams(window.location.search).get("force_android_block");
  // Once the user has submitted a fallback file, we want the loading
  // overlay / processing screen to take over — so we stop blocking the
  // normal render flow with this early return.
  if (
    !fallbackInProgress &&
    (forceAndroidBlock === "os_toggle" ||
      forceAndroidBlock === "chrome_permission" ||
      forceAndroidBlock === "true")
  ) {
    return (
      <AndroidCameraBlockPortal
        locale={faceLocale}
        requestId={settings.request_id}
        captureType="face"
        isBrowserHandoffSession={false}
        androidVariant={
          forceAndroidBlock === "chrome_permission"
            ? "chrome_permission_legacy"
            : "os_toggle_12plus"
        }
        onPhotoFallback={handleSelfieFallback}
        captureMode="user"
      />
    );
  }

  // Show standard handoff first — when the user is on desktop
  // without the continue-on-desktop opt-in, they should be sent to
  // mobile via QR, no camera involved here.
  if (!onMobile && !should_show_continue_on_desktop) {
    return <Handoff nextPath="agematch/estimate" />;
  }

  // Mount VeratadCapture immediately so the user lands on the real
  // camera framing (oval guides, feedback area, etc.) instead of a
  // separate instructions screen. The start drawer overlays the
  // bottom of the capture view until the user taps Get Started; on
  // tap, `hasStarted` flips true and the drawer is removed,
  // surfacing the full capture UI. Camera permissions are requested
  // by VeratadCapture on its mount — the drawer is just visual
  // instruction over the live view.
  return (
    <Box style={{ position: "relative", height: "100%" }}>
      <VeratadCapture
        onCapture={handleVeratadCapture}
        onError={handleError}
        onReady={handleVeratadReady}
        onCameraAllowed={handleCameraAllowed}
        onPhotoFallback={handleSelfieFallback}
        // Keep the camera dormant — including no getUserMedia call,
        // no permission prompt, no 7s timeout — until the user taps
        // Get Started on the overlaid start drawer.
        paused={!hasStarted}
        // Stream face-detection state up so the drawer's three
        // prep icons can light up as each gate is satisfied.
        onGatingChange={setGatingState}
        bottomInset={hasStarted && !sideLayout ? drawerInset : 0}
        // Sideways the panel sits beside the oval in both phases, so
        // the oval moves left of it from the start.
        rightInset={sideLayout ? drawerInset : 0}
      />
      {/* Drawer is always rendered. Layout stays constant; only
          the title visibility, icon lit/dim states, and button
          visibility shift between paused and live. Directive
          feedback ("Move closer", etc.) lives inside the camera
          viewport itself. */}
      {startDrawer}
    </Box>
  );
};

export default AgeEstimation;
